import { Component, computed, input, signal, type InputSignal, type Signal, type WritableSignal } from "@angular/core";

/**
 * A tenant's logo, or its initial when there is no logo to show.
 *
 * A bare `<img [src]="logoUrl">` renders the browser's broken-image icon both
 * while a logo URL is still blank (onboarding, before one is typed) and when
 * one fails to load. Neither is an error the viewer can do anything about, and
 * the icon reads as one. This draws the organization's first letter instead,
 * the way the Davaco tile does.
 *
 * Sized entirely by the class the caller puts on the host, as the `<img>` was:
 * the fallback is a square SVG that scales to whatever height it is given, so
 * no call site needs a size input.
 */
@Component({
  selector: "app-brand-logo",
  standalone: true,
  templateUrl: "./brand-logo.component.html",
  styleUrl: "./brand-logo.component.scss",
})
export class BrandLogoComponent {

  public readonly src: InputSignal<string | null | undefined> =
    input<string | null | undefined>(null);

  /** Names the logo for assistive tech, and supplies the fallback's initial. */
  public readonly name: InputSignal<string> = input<string>("");

  /** Fallback tile colors. Default to the live brand layer. */
  public readonly background: InputSignal<string> = input<string>("var(--brand-primary)");

  public readonly foreground: InputSignal<string> = input<string>("var(--brand-on-primary)");

  /**
   * The source that failed, rather than a flag, so typing a NEW url in the
   * onboarding form tries again instead of staying on the initial.
   */
  private readonly failedSrc: WritableSignal<string | null> = signal<string | null>(null);

  public readonly url: Signal<string | null> = computed((): string | null => {
    const value: string = (this.src() ?? "").trim();
    return value.length > 0 && value !== this.failedSrc() ? value : null;
  });

  public readonly initial: Signal<string> = computed(
    (): string => this.name().trim()
.charAt(0)
.toUpperCase() || "?"
  );

  public onError(): void {
    this.failedSrc.set((this.src() ?? "").trim());
  }
}
